import React, { useMemo, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useClub } from '../context/ClubContext';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameById } from '../utils/courtDisplay';

const CourtView = ({ 
  bookings, 
  selectedSport, 
  onSlotClick, 
  selectedDate, 
  isLoading, 
  onBookingSelect, 
  courts, 
  localTimeSlots,
  user,
  onShareBooking,
  onNextDay,
  onPreviousDay,
  canGoPreviousDay = false
}) => {
  const { getCategoryStyle, getIconBackgroundColor } = useClub();
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const selectedDateString = selectedDate.toLocaleDateString('en-CA');

  // Filter courts based on selected sport
  const filteredCourts = selectedSport === 'All Courts'
    ? courts
    : courts.filter(court => court.court.startsWith(selectedSport));

  // Get all unique time slots across all filtered courts
  const getAllTimeSlots = () => {
    const allSlots = new Set();
    filteredCourts.forEach(court => {
      if (localTimeSlots && localTimeSlots[court.court]) {
        localTimeSlots[court.court].forEach(slot => allSlots.add(slot));
      }
    });
    return Array.from(allSlots).sort();
  };

  const timeSlots = getAllTimeSlots();

  const parseTimeToMinutes = (time) => {
    const [hour, minute] = time.split(':').map(Number);
    return (hour * 60) + minute;
  };

  const isBlockBooking = (booking) => {
    if (booking.bookingDate === 'BLOCK') return true;
    if (!booking.isRecurring || !booking.recurringRule) return false;
    try {
      const recurringRule = typeof booking.recurringRule === 'string'
        ? JSON.parse(booking.recurringRule)
        : booking.recurringRule;
      return recurringRule?.duration === -1;
    } catch {
      return false;
    }
  };

  const bookingKey = (booking) => `${booking.bookingId || `${booking.court}-${booking.bookingDate}-${booking.startTime}-${booking.endTime}`}`;

  const { slotBookingMap, segmentStartMap } = useMemo(() => {
    const slotMap = new Map();
    const startMap = new Map();

    filteredCourts.forEach((court) => {
      const courtName = court.court;
      const courtSlots = (localTimeSlots?.[courtName] || []).slice().sort();
      const courtBookings = bookings.filter(
        (b) => b.court === courtName && b.bookingDate === selectedDateString
      );

      const regularBookings = courtBookings.filter((b) => !isBlockBooking(b));
      const blockBookings = courtBookings.filter((b) => isBlockBooking(b));
      const orderedBookings = [...regularBookings, ...blockBookings];

      orderedBookings.forEach((booking) => {
        const bookingStart = parseTimeToMinutes(booking.startTime);
        const bookingEnd = parseTimeToMinutes(booking.endTime);

        courtSlots.forEach((slot) => {
          const slotMin = parseTimeToMinutes(slot);
          if (slotMin < bookingStart || slotMin >= bookingEnd) return;

          const mapKey = `${courtName}#${slot}`;
          if (!slotMap.has(mapKey)) {
            slotMap.set(mapKey, booking);
          }
        });
      });

      let index = 0;
      while (index < courtSlots.length) {
        const startSlot = courtSlots[index];
        const startBooking = slotMap.get(`${courtName}#${startSlot}`);
        if (!startBooking) {
          index += 1;
          continue;
        }

        let span = 1;
        const startBookingId = bookingKey(startBooking);

        while (index + span < courtSlots.length) {
          const nextSlot = courtSlots[index + span];
          const nextBooking = slotMap.get(`${courtName}#${nextSlot}`);
          if (!nextBooking || bookingKey(nextBooking) !== startBookingId) break;
          span += 1;
        }

        startMap.set(`${courtName}#${startSlot}`, {
          booking: startBooking,
          span
        });

        index += span;
      }
    });

    return {
      slotBookingMap: slotMap,
      segmentStartMap: startMap
    };
  }, [bookings, filteredCourts, localTimeSlots, selectedDateString]);

  // Check if a specific court has a particular time slot available
  const isTimeSlotAvailableForCourt = (courtName, localTime) => {
    const result = localTimeSlots && localTimeSlots[courtName] && localTimeSlots[courtName].includes(localTime);
    return result;
  };

  const getCurrentClubTime = () => {
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    return new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
  };

  const getBookingDetails = (court, localTime) => slotBookingMap.get(`${court}#${localTime}`) || null;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (onNextDay) onNextDay();
    },
    onSwipedRight: () => {
      if (canGoPreviousDay && onPreviousDay) {
        onPreviousDay();
      }
    },
    trackMouse: false,
    preventScrollOnSwipe: true,
    delta: 50,
  });
  
  const handleMouseDown = (court, localTime) => {
    setIsSelecting(true);
    setSelectedSlots([{ court, localTime }]);
  };

  const handleMouseEnter = (court, localTime) => {
    if (isSelecting) {
      setSelectedSlots((prev) => {
        const lastSlot = prev[prev.length - 1];
        if (lastSlot && lastSlot.court === court) {
          return [...prev, { court, localTime }];
        }
        return prev;
      });
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    if (selectedSlots.length > 0) {
      // Sort selected slots to find the earliest and latest times
      const sortedSlots = selectedSlots.sort((a, b) => {
        const aTime = new Date(`1970-01-01T${a.localTime}:00`);
        const bTime = new Date(`1970-01-01T${b.localTime}:00`);
        return aTime - bTime; // Sort by time
      });

      const startTime = sortedSlots[0].localTime; // First slot's time
      const endTime = sortedSlots[sortedSlots.length - 1].localTime; // Last slot's time
      const [endHour, endMinute] = endTime.split(':').map(Number);

      // Get the slot duration for the selected court
      const selectedCourtData = courts.find(court => court.court === selectedSlots[0].court);
      const slotDuration = selectedCourtData ? parseInt(selectedCourtData.slotDuration, 10) : 30; // Default to 30 if not found

      // Calculate adjusted end time based on slot duration
      const adjustedEndTime = new Date();
      adjustedEndTime.setHours(endHour, endMinute, 0, 0);
      adjustedEndTime.setMinutes(adjustedEndTime.getMinutes() + slotDuration); // Increment by slot duration

      const booking = getBookingDetails(selectedSlots[0].court, startTime);
      
      if (booking) {
        onBookingSelect(booking);
      } else {
        onSlotClick({
          court: selectedSlots[0].court,
          date: selectedDateString,
          startTime,
          endTime: adjustedEndTime.toTimeString().slice(0, 5) // Format as HH:MM
        });
      }
      
      setSelectedSlots([]);
    }
  };

  const isSlotSelected = (court, localTime) => {
    return selectedSlots.some(slot => slot.court === court && slot.localTime === localTime);
  };

  const formatCourtName = (court) => {
    const aliasLabel = getCourtDisplayNameById(court, filteredCourts, '').trim();
    if (aliasLabel) return aliasLabel;
    const parts = court.split('-');
    const sportName = formatSportDisplayName(parts[0]);
    const courtNumber = parts[1];
    
    // Get unique sports from filtered courts to determine if we should show sport name or "Court"
    const uniqueSports = [...new Set(filteredCourts.map(c => c.court.split('-')[0]))];
    const displayName = uniqueSports.length === 1 ? 'Court' : sportName;
    
    return `${displayName} ${courtNumber}`;
  };

  // Show message if no courts available
  if (filteredCourts.length === 0) {
    return (
      <div className="shadow-md rounded-lg bg-white p-8 text-center">
        <p className="text-slate-600">No courts available for the selected sport.</p>
      </div>
    );
  }

  const currentTime = getCurrentClubTime();
  const currentClubDateString = currentTime.toLocaleDateString('en-CA');
  const currentClubMinutes = (currentTime.getHours() * 60) + currentTime.getMinutes();

  // Today: scroll to the next bookable window, but keep the row for the slot that *contains* now
  // (e.g. 14:05 inside 14:00–14:30) so in-progress bookings stay visible for access control / details.
  const slotDurationMinutes = Math.max(
    5,
    parseInt(String(filteredCourts[0]?.slotDuration ?? '30'), 10) || 30
  );
  const slotMinutesSorted = [...new Set(timeSlots.map(parseTimeToMinutes))].sort((a, b) => a - b);
  let visibleTimeSlots = timeSlots;
  if (selectedDateString === currentClubDateString && slotMinutesSorted.length > 0) {
    const containingSlotMin = slotMinutesSorted.find(
      (slotMin) =>
        slotMin <= currentClubMinutes && currentClubMinutes < slotMin + slotDurationMinutes
    );
    const firstSlotAtOrAfterNow = slotMinutesSorted.find((m) => m >= currentClubMinutes);
    const startMin =
      containingSlotMin !== undefined
        ? containingSlotMin
        : firstSlotAtOrAfterNow !== undefined
          ? firstSlotAtOrAfterNow
          : slotMinutesSorted[0];
    visibleTimeSlots = timeSlots.filter((slot) => parseTimeToMinutes(slot) >= startMin);
  }

  // Show message if no time slots available
  if (visibleTimeSlots.length === 0) {
    return (
      <div className="shadow-md rounded-lg bg-white p-8 text-center">
        <p className="text-slate-600">
          {selectedDateString === currentClubDateString
            ? 'No upcoming time slots available for today.'
            : 'No time slots available for the selected courts.'}
        </p>
      </div>
    );
  }

  // Create grid template columns dynamically - only court columns
  const gridTemplateColumns = `repeat(${filteredCourts.length}, minmax(0, 1fr))`;
  const gridTemplateRows = `repeat(${visibleTimeSlots.length}, minmax(40px, auto))`;

  return (
    <div className="shadow-md rounded-lg" {...swipeHandlers}>
      {/* Header row with court names */}
      <div className="sticky top-0 z-20 bg-white rounded-t-lg border-t border-slate-400 border-b border-slate-400">
        <div 
          className="grid items-center relative"
          style={{ 
            gridTemplateColumns,
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {/* Court headers */}
          {filteredCourts.map((court, courtIndex) => (
            <div 
              key={court.court} 
              className={`p-2 text-center text-xs sm:text-sm font-medium text-slate-800 overflow-hidden text-ellipsis ${
                courtIndex < filteredCourts.length - 1 ? 'border-r border-slate-200' : ''
              }`}
              style={{ minWidth: 0 }}
            >
              {formatCourtName(court.court)}
            </div>
          ))}

        </div>
      </div>

      {/* Time slot grid */}
      <div 
        className={`grid bg-white ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        style={{ 
          gridTemplateColumns,
          gridTemplateRows,
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        {filteredCourts.map((court, courtIndex) => {
          return visibleTimeSlots.map((localTime, rowIndex) => {
            const isSlotAvailable = isTimeSlotAvailableForCourt(court.court, localTime);
            const slotKey = `${court.court}#${localTime}`;
            const booking = isSlotAvailable ? (slotBookingMap.get(slotKey) || null) : null;
            const segment = segmentStartMap.get(slotKey);

            if (booking && !segment) {
              const previousSlot = visibleTimeSlots[rowIndex - 1];
              const previousBooking = previousSlot
                ? slotBookingMap.get(`${court.court}#${previousSlot}`)
                : null;

              // Only skip rendering if this is a continuation of an already-rendered booking segment.
              if (previousBooking && bookingKey(previousBooking) === bookingKey(booking)) {
                return null;
              }
            }

            const [hour, minute] = localTime.split(':').map(Number);
            const slotTime = new Date(selectedDate);
            slotTime.setHours(hour, minute, 0, 0);

            let disablePastInteraction = slotTime < currentTime;
            if (booking) {
              const bookingEnd = new Date(`${booking.bookingDate}T${booking.endTime}:00`);
              disablePastInteraction = bookingEnd < currentTime;
            }

            const isBookable = isSlotAvailable && !booking && slotTime >= currentTime;
            const disableButton = !isSlotAvailable || (!booking && disablePastInteraction);
            const rowSpan = segment?.span || (() => {
              if (!booking) return 1;
              let span = 1;
              const currentBookingId = bookingKey(booking);
              while (rowIndex + span < visibleTimeSlots.length) {
                const nextSlot = visibleTimeSlots[rowIndex + span];
                const nextBooking = slotBookingMap.get(`${court.court}#${nextSlot}`);
                if (!nextBooking || bookingKey(nextBooking) !== currentBookingId) break;
                span += 1;
              }
              return span;
            })();
            const allowWrappedLabel = Boolean(booking) && rowSpan >= 3;
            const showCenteredPublicIcon = Boolean(booking?.public) && rowSpan >= 3;

            const categoryClasses = getCategoryStyle({ category: booking?.category, court: court.court }).split(' ');
            const recurringBorderL = booking?.isRecurring
              ? getCategoryStyle({ category: 'Recurring', court: court.court }).split(' ').find(cls => cls.startsWith('border-l-'))
              : '';
            const filteredCategoryClasses = booking?.isRecurring
              ? categoryClasses.filter(cls => !cls.startsWith('border-l-')).join(' ')
              : categoryClasses.join(' ');

            const categoryBorderColor = categoryClasses.find(cls => cls.startsWith('border-') && !cls.startsWith('border-l-')) || 'border-blue-400';
            const iconBgColor = getIconBackgroundColor(categoryBorderColor);

            return (
              <div
                key={`${court.court}-${localTime}`}
                className={`min-h-[40px] p-1 border-r border-b border-slate-100 ${
                  !booking && isSlotSelected(court.court, localTime) ? 'hover:bg-booking-hover-available' : ''
                }`}
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  gridColumn: courtIndex + 1,
                  gridRow: `${rowIndex + 1} / span ${rowSpan}`,
                  zIndex: booking ? 10 : 1
                }}
                onMouseDown={() => !disableButton && !booking && handleMouseDown(court.court, localTime)}
                onMouseEnter={() => !disableButton && !booking && handleMouseEnter(court.court, localTime)}
                onMouseUp={handleMouseUp}
              >
                <button
                  className={`
                    w-full h-full p-1 rounded text-xs transition border relative
                    ${isSlotAvailable ? filteredCategoryClasses : getCategoryStyle({ category: 'Available', court: court.court })}
                    ${disablePastInteraction ? 'cursor-not-allowed opacity-50' : (!disableButton ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                    ${!booking && isSlotSelected(court.court, localTime) ? 'bg-emerald-100' : ''}
                    ${booking?.isRecurring ? 'border-l-4' : ''}
                    ${recurringBorderL}
                  `}
                  disabled={disableButton}
                  onClick={() => {
                    if (disableButton) return;
                    if (booking) {
                      onBookingSelect(booking);
                    }
                  }}
                >
                  <span className="flex items-center justify-center gap-1 overflow-visible relative">
                    {booking && booking.status !== 'confirmed' && (
                      <span className="text-red-600 mr-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                          <circle cx="12" cy="12" r="10" fill="currentColor" />
                          <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                          <circle cx="12" cy="17" r="1.2" fill="white" />
                        </svg>
                      </span>
                    )}
                    {booking?.public && user && (() => {
                      try {
                        const publicInfo = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
                        return publicInfo?.pending && publicInfo.pending[user.uuid];
                      } catch {
                        return false;
                      }
                    })() && (
                      <span className="text-red-600 mr-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                          <circle cx="12" cy="12" r="10" fill="currentColor" />
                          <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                          <circle cx="12" cy="17" r="1.2" fill="white" />
                        </svg>
                      </span>
                    )}
                    <span className="flex flex-col items-center w-full min-w-0 leading-tight">
                      <span
                        className={`block w-full min-w-0 text-center text-xs sm:text-sm md:text-base ${allowWrappedLabel ? 'whitespace-normal break-words' : 'overflow-hidden text-ellipsis whitespace-nowrap'}`}
                        style={allowWrappedLabel ? {
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        } : undefined}
                      >
                        {booking ? (booking.displayLabel || booking.createdByName) : ''}
                      </span>
                      {booking && rowSpan > 1 && (
                        <span className="block w-full min-w-0 text-center text-xs sm:text-xs md:text-xs mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                          {`${booking.startTime} - ${booking.endTime}`}
                        </span>
                      )}
                      {showCenteredPublicIcon && (
                        <span className="mt-1 flex items-center justify-center gap-1">
                          <span className={`w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center`}>
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 512 512">
                              <path fillRule="evenodd" clipRule="evenodd" d="M224,448c0,0-32,0-32-32s32-128,160-128s160,96,160,128s-32,32-32,32H224z M352,256c53,0,96-43,96-96s-43-96-96-96s-96,43-96,96S299,256,352,256z M166.9,448c-4.8-10-7.1-20.9-6.9-32c0-43.4,21.8-88,62-119c-20.1-6.2-41-9.2-62-9C32,288,0,384,0,416s32,32,32,32H166.9z M144,256c44.2,0,80-35.8,80-80s-35.8-80-80-80s-80,35.8-80,80S99.8,256,144,256z"/>
                            </svg>
                          </span>
                          <span
                            className={`w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center cursor-pointer`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onShareBooking?.(booking);
                            }}
                          >
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="18" cy="5" r="2"></circle>
                              <circle cx="6" cy="12" r="2"></circle>
                              <circle cx="18" cy="19" r="2"></circle>
                              <path d="M8 12l8-6"></path>
                              <path d="M8 12l8 6"></path>
                            </svg>
                          </span>
                        </span>
                      )}
                    </span>
                  </span>
                  {booking?.public && !showCenteredPublicIcon && (
                    <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center`}>
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 512 512">
                        <path fillRule="evenodd" clipRule="evenodd" d="M224,448c0,0-32,0-32-32s32-128,160-128s160,96,160,128s-32,32-32,32H224z M352,256c53,0,96-43,96-96s-43-96-96-96s-96,43-96,96S299,256,352,256z M166.9,448c-4.8-10-7.1-20.9-6.9-32c0-43.4,21.8-88,62-119c-20.1-6.2-41-9.2-62-9C32,288,0,384,0,416s32,32,32,32H166.9z M144,256c44.2,0,80-35.8,80-80s-35.8-80-80-80s-80,35.8-80,80S99.8,256,144,256z"/>
                      </svg>
                    </span>
                  )}
                  {booking?.public && !showCenteredPublicIcon && (
                    <span
                      className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center cursor-pointer`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onShareBooking?.(booking);
                      }}
                    >
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="2"></circle>
                        <circle cx="6" cy="12" r="2"></circle>
                        <circle cx="18" cy="19" r="2"></circle>
                        <path d="M8 12l8-6"></path>
                        <path d="M8 12l8 6"></path>
                      </svg>
                    </span>
                  )}
                  {!booking && (
                    <span className={`block text-xs sm:text-sm md:text-base ${isBookable ? '' : 'invisible'}`}>
                      {localTime}
                    </span>
                  )}
                </button>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
};

export default CourtView;
